import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useUnresolvedAlerts } from "@/shared/api/hooks/alerts";
import { qk } from "@/shared/api/keys";
import { alarmLightSet, hasAlarmLight } from "@/shared/native/alarmLight";
import { useAlarmSettings } from "./useAlarmSettings";

/** 켠 뒤 자동 소등(초). 보드는 터치가 없어 아무도 못 끈다 — 1분 뒤 스스로 꺼진다. */
export const ALARM_LIGHT_AUTO_OFF_SEC = 60;
/** 꺼진 상태를 주기적으로 다시 쓰는 간격(ms) — 장치가 리셋돼도 켜진 채 남지 않게. */
const HEARTBEAT_MS = 30_000;
/** 보드에서 경보 목록을 당겨 오는 간격(ms). 폰 앱에서 「확인」하면 이 안에 등이 꺼진다. */
const BOARD_POLL_MS = 10_000;

/** 한 번 켠 적 있는 경보 id — 같은 경보로 1분 뒤 다시 켜지지 않게. 리마운트에도 남아야 하므로 모듈 수준. */
const litFor = new Set<number>();

/**
 * 보드 직결 경광등을 경보 상태에 묶는다 — AppShell 에서 한 번 마운트.
 *
 * 켜짐 조건은 경보 오버레이(AlarmCenter)의 「대기 중」 판정과 **같은 식**이다:
 * 미확인(open)·미해제·임계(useAlarmSettings.threshold) 이상 경보. 판정식이 둘로
 * 갈라지면 「화면은 경보인데 등은 꺼짐」이 난다.
 *
 * 동작
 *   · **새 경보가 생길 때** 켠다(상승 엣지). 네이티브가 1분 뒤 스스로 끈다.
 *     같은 경보로는 다시 켜지 않는다 — 1분마다 깜빡이며 되살아나면 「끄는 방법이 없는」 상태가 된다.
 *   · 경보가 전부 확인·해제되면 즉시 끈다. 보드는 10초마다 목록을 당기므로
 *     **스마트폰 앱에서 「확인」을 누르면 10초 안에 꺼진다.**
 *   · 폰·웹에서는 `hasAlarmLight()` 가 false 라 아무것도 하지 않는다.
 */
export function useAlarmLight(): void {
  const enabled = hasAlarmLight();
  const threshold = useAlarmSettings((s) => s.threshold);
  const { alerts, ready } = useUnresolvedAlerts();
  const qc = useQueryClient();

  // 켜야 할 경보 id 들 — 정렬해 문자열로 비교(배열 참조 변화로 효과가 헛돌지 않게).
  // ⛔ 설비 무응답(health)·모의훈련 경보로는 켜지 않는다(2026-09-28 음원 점검). 경광등은 「지금 가스 위험」
  //    신호다 — 매일 밤 LTE 끊김 무응답 경보마다 켜지면 진짜 경보 때 믿지 않게 된다. 훈련은 서버 정책대로
  //    화면·음성 안에서만(현장 경광등은 매니져가 따로 판단). 오버레이는 두 경우도 계속 뜬다.
  const activeKey = useMemo(
    () =>
      enabled && ready
        ? alerts
            .filter((a) => a.id != null && a.state === "open" && !a.cleared_at && (a.level ?? 0) >= threshold
              && a.kind !== "health" && !a.is_drill)
            .map((a) => a.id!)
            .sort((a, b) => a - b)
            .join(",")
        : "",
    [enabled, ready, alerts, threshold],
  );
  const active = activeKey.length > 0;

  useEffect(() => {
    if (!enabled || !ready) return;
    if (!active) {
      litFor.clear();
      void alarmLightSet(false);
      return;
    }
    const ids = activeKey.split(",").map(Number);
    const fresh = ids.some((id) => !litFor.has(id));
    if (!fresh) return;
    ids.forEach((id) => litFor.add(id));
    void alarmLightSet(true, ALARM_LIGHT_AUTO_OFF_SEC);
  }, [enabled, ready, active, activeKey]);

  // 꺼진 상태 하트비트 — 켜진 상태는 네이티브 자동 소등에 맡긴다.
  useEffect(() => {
    if (!enabled || !ready || active) return;
    const id = setInterval(() => void alarmLightSet(false), HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [enabled, ready, active]);

  // 보드는 폴링을 빠르게 — 폰에서 확인·해제한 것이 30초 기본 주기보다 빨리 반영되게.
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(
      () => void qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
      BOARD_POLL_MS,
    );
    return () => clearInterval(id);
  }, [enabled, qc]);

  // 셸이 내려갈 때(로그아웃 등) 켜진 등을 남기지 않는다.
  useEffect(() => {
    if (!enabled) return;
    return () => {
      litFor.clear();
      void alarmLightSet(false);
    };
  }, [enabled]);
}
